<template>
  <div class="transaction-page">
    <!-- Loading indicator -->
    <div v-if="transactionStore.loading" class="flex flex-column align-items-center py-5">
      <i class="pi pi-spin pi-spinner text-6xl text-primary mb-4"></i>
      <p>Loading transaction data...</p>
    </div>

    <div v-else>
      <!-- Header with action buttons -->
      <div class="flex align-items-center justify-content-between mb-4">
        <div class="flex flex-column">
          <h2 class="text-2xl font-semibold m-0">Sales Ticket</h2>
          <!-- Date display/edit -->
          <div v-if="!showDatePicker" class="mt-2">
            <span
              v-if="localTransactionDate"
              class="text-sm text-600 cursor-pointer"
              @click="showDatePicker = true"
            >
              {{ formatDate(localTransactionDate) }}
            </span>
            <span v-else class="text-sm text-600 cursor-pointer" @click="showDatePicker = true">
              Click to set date
            </span>
          </div>
          <div v-else class="mt-2" style="width: 200px">
            <DatePicker
              v-model="localTransactionDate"
              id="transactionDate"
              :showIcon="true"
              dateFormat="yy-mm-dd"
              class="w-full"
              @date-select="onDateSelect"
              @hide="showDatePicker = false"
            />
          </div>
        </div>
        <div class="flex gap-2">
          <Button label="Cancel" icon="pi pi-times" class="p-button-outlined" @click="cancel" />
          <Button label="Save Transaction" icon="pi pi-check" @click="saveTransaction" />
        </div>
      </div>

      <!-- Organization Selection -->
      <Card class="mb-4">
        <template #header>
          <div class="flex align-items-center justify-content-between">
            <div class="flex align-items-center">
              <i class="pi pi-building mr-2"></i>
              <h5 class="m-0">Customer Company</h5>
            </div>
          </div>
        </template>
        <template #content>
          <!-- Collapsed view: just the customer name -->
          <div
            v-if="selectedOrganizationId && !showOrganizationDetails"
            class="text-lg font-semibold cursor-pointer"
            @click="showOrganizationDetails = true"
          >
            {{ selectedOrganization?.name || transaction.organizationSnapshot?.name || 'Unknown' }}
          </div>

          <!-- Expanded view: full form -->
          <div v-else class="grid">
            <div class="col-12 mb-3">
              <span class="p-float-label">
                <AutoComplete
                  v-model="selectedOrganization"
                  :suggestions="filteredOrganizationOptions"
                  @complete="searchOrganizations"
                  @item-select="onOrganizationSelect"
                  field="name"
                  dropdown
                  class="w-full"
                  optionLabel="name"
                />
                <label for="organizationSelect">Customer Company</label>
              </span>
            </div>

            <!-- Display selected organization information -->
            <div
              v-if="selectedOrganizationId"
              class="col-12 mb-3 p-2 border-1 surface-border border-round"
            >
              <div class="flex flex-column gap-2">
                <div
                  class="text-lg font-semibold cursor-pointer flex align-items-center gap-2"
                  @click="showOrganizationDetails = false"
                >
                  <i class="pi pi-chevron-down text-sm"></i>
                  <span>{{
                    selectedOrganization?.name ||
                    transaction.organizationSnapshot?.name ||
                    'Unknown'
                  }}</span>
                </div>
                <div class="flex flex-column gap-2 pl-4">
                  <div
                    v-if="selectedOrganization?.email || transaction.organizationSnapshot?.email"
                    class="flex align-items-center gap-2"
                  >
                    <i class="pi pi-envelope text-sm"></i>
                    <span>{{
                      selectedOrganization?.email || transaction.organizationSnapshot?.email
                    }}</span>
                  </div>
                  <div
                    v-if="selectedOrganization?.phone || transaction.organizationSnapshot?.phone"
                    class="flex align-items-center gap-2"
                  >
                    <i class="pi pi-phone text-sm"></i>
                    <span>{{
                      selectedOrganization?.phone || transaction.organizationSnapshot?.phone
                    }}</span>
                  </div>
                  <div
                    v-if="
                      selectedOrganization?.address ||
                      transaction.organizationSnapshot?.streetAddress
                    "
                    class="flex align-items-center gap-2"
                  >
                    <i class="pi pi-map-marker text-sm"></i>
                    <span>{{
                      selectedOrganization?.address ||
                      [
                        transaction.organizationSnapshot?.streetAddress,
                        transaction.organizationSnapshot?.city,
                        transaction.organizationSnapshot?.state,
                        transaction.organizationSnapshot?.postalCode,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 flex justify-content-between">
              <Button
                label="Create New Company"
                icon="pi pi-plus"
                class="p-button-sm"
                @click="showCreateOrganizationDialog = true"
              />

              <!-- Edit organization button -->
              <Button
                v-if="selectedOrganizationId"
                label="Edit Company"
                icon="pi pi-pencil"
                class="p-button-sm p-button-outlined"
                @click="openEditOrganizationDialog"
              />
            </div>
          </div>
        </template>
      </Card>

      <!-- Transaction Items -->
      <TransactionItemList
        :items="transaction.items || []"
        :commodity-types="commodityTypes"
        @add-item="addNewItem"
        @edit-item="editItem"
        @remove-item="removeItem"
        @bulk-update="handleBulkUpdate"
      />

      <TransactionItemFormDialog
        v-model:visible="showItemDialog"
        :commodity-types="commodityTypes"
        :item-index="editingItemIndex"
        @save="onItemSaved"
        @cancel="onItemCancelled"
      />

      <!-- Notes -->
      <Card class="mb-4">
        <template #header>
          <div class="flex align-items-center">
            <i class="pi pi-info-circle mr-2"></i>
            <h5 class="m-0">Notes</h5>
          </div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12">
              <span class="p-float-label">
                <Textarea v-model="localNotes" rows="3" id="notes" class="w-full" />
                <label for="notes">Notes</label>
              </span>
            </div>
          </div>
        </template>
      </Card>

      <!-- Create Organization Dialog -->
      <OrganizationDialog
        v-model:visible="showCreateOrganizationDialog"
        :organization-data="newOrganization"
        :is-editing="false"
        @organization-saved="onOrganizationCreated"
        @cancel="showCreateOrganizationDialog = false"
      />

      <!-- Edit Organization Dialog -->
      <OrganizationDialog
        v-model:visible="showEditOrganizationDialog"
        :organization-data="editingOrganization"
        :is-editing="true"
        @organization-saved="onOrganizationUpdated"
        @cancel="showEditOrganizationDialog = false"
      />

      <!-- Transaction Summary -->
      <Card class="mb-4 border-green-200 surface-card">
        <template #header>
          <div class="flex align-items-center bg-green-50 pb-0">
            <i class="pi pi-calculator text-green-500 mr-2"></i>
            <h5 class="m-0 text-green-700">Transaction Summary</h5>
          </div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6">
              <div class="text-lg mb-2">
                Total Items:
                <span class="font-semibold">{{ (transaction.items || []).length }}</span>
              </div>
              <div class="text-lg mb-2">
                Total Weight:
                <span class="font-semibold">{{ transactionModel?.totalWeight || 0 }} lbs</span>
              </div>
            </div>
            <div class="col-12 md:col-6 text-right">
              <div class="text-2xl text-green-600 font-bold">
                Total Amount:
                {{
                  TransactionModel.formatCurrency(parseFloat(transactionModel?.totalAmount || '0'))
                }}
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useCommodityTypeService } from '@/services/commodityTypeService'
import { useToast } from 'primevue/usetoast'
import { formatDate } from '@/utils/formatters'

// Import components
import TransactionItemFormDialog from '@/components/transaction/TransactionItemFormDialog.vue'
import TransactionItemList from '@/components/TransactionItemList.vue'
import { createTransaction } from '@/factories/transactionFactory'
import OrganizationDialog from '@/components/organization/OrganizationDialog.vue'

// Import services and composables
import { useOrganizationStore } from '@/stores/v2/organization'
import { createOrganization } from '@/factories/organizationFactory'
import { useTransactionStore } from '@/stores/transactionStoreOld'
import { TransactionModel } from '@/models/TransactionModel'
import type { Organization } from '@/types/Organization'

// PrimeVue components
import Card from 'primevue/card'
import Button from 'primevue/button'
import AutoComplete from 'primevue/autocomplete'
import DatePicker from 'primevue/datepicker'
import Textarea from 'primevue/textarea'
import { useConfirm } from 'primevue/useconfirm'
import type { CommodityType } from '@/types/CommodityType'

const toast = useToast()
const router = useRouter()
const route = useRoute()
const transactionStore = useTransactionStore()
const organizationStore = useOrganizationStore()
const commodityTypeService = useCommodityTypeService()
const confirm = useConfirm()

// Local UI state
const showCreateOrganizationDialog = ref(false)
const showEditOrganizationDialog = ref(false)
const showItemDialog = ref(false)
const editingItemIndex = ref(-1)
const showOrganizationDetails = ref(false)
const showDatePicker = ref(false)

const commodityTypes = ref<CommodityType[]>([])
const selectedOrganization = ref<Organization | null>(null)
const filteredOrganizationOptions = ref<Organization[]>([])
const editingOrganization = ref<Partial<Organization>>({})
const selectedOrganizationId = ref<string>('')

// Transaction details local state
const localTransactionDate = ref<Date>(new Date())
const localNotes = ref<string | undefined>(undefined)

// Computed properties
const transactionId = computed(() => route.params.id as string)
const transaction = computed(() => transactionStore.draft || createTransaction())

const transactionModel = computed(() => {
  if (transaction.value) {
    return new TransactionModel(transaction.value)
  }
  return null
})

// Initialize new organization data
const newOrganization = ref(
  createOrganization({
    type: 'customer',
    status: 'active',
  }),
)

// v-model proxy for notes
const notesProxy = computed<string | undefined>({
  get() {
    return (transaction.value as any).notes ?? undefined
  },
  set(value) {
    if (transaction.value.id) {
      transactionStore.updateDraft({ notes: value ?? null })
    }
  },
})

// Watch local transaction details and update store
watch(localTransactionDate, (value) => {
  if (transaction.value.id && value) {
    transactionStore.updateDraft({ transaction_date: value })
  }
})

// Handle date selection from datepicker
function onDateSelect() {
  // The v-model will update localTransactionDate automatically
  // Close the datepicker after selection
  showDatePicker.value = false
}

watch(localNotes, (value) => {
  if (transaction.value.id) {
    transactionStore.updateDraft({ notes: value ?? null })
  }
})

// Methods
async function searchOrganizations(event: { query: string }) {
  const query = String(event.query || '')
    .trim()
    .toLowerCase()
  try {
    const search = await organizationStore.search()
    const results = await search.byType('customer').byText(query).run()
    filteredOrganizationOptions.value = results
  } catch (e) {
    console.error('Organization search failed:', e)
    filteredOrganizationOptions.value = []
  }
}

async function onOrganizationSelect(event: { value: Organization }) {
  if (!event.value.id) return
  selectedOrganizationId.value = event.value.id
  showOrganizationDetails.value = false // Collapse the card when a customer is selected

  // Update the transaction in the store with the organizationSnapshot
  if (transaction.value.id) {
    const org = event.value
    await transactionStore.updateDraft({
      organizationId: org.id,
      organizationSnapshot: {
        name: org.name || '',
        phone: org.phone || '',
        email: org.email || '',
        streetAddress: org.address || '',
        city: org.city || '',
        state: org.state || '',
        postalCode: org.postalCode || '',
        country: org.country || '',
      },
    })
  }
}

async function onOrganizationCreated(createdOrg: Organization) {
  // Add the new organization to the store
  const newId = await organizationStore.create(createdOrg)
  const newOrg = (await organizationStore.get(newId)) as Organization

  // Select the newly created organization
  if (newOrg.id) selectedOrganizationId.value = newOrg.id
  selectedOrganization.value = newOrg
  showOrganizationDetails.value = false // Collapse the card when a customer is created

  // Update the transaction in the store
  if (transaction.value.id) {
    transactionStore.updateDraft({
      organizationId: newOrg.id,
      organizationSnapshot: {
        name: newOrg.name || '',
        phone: newOrg.phone || '',
        email: newOrg.email || '',
        streetAddress: newOrg.address || '',
        city: newOrg.city || '',
        state: newOrg.state || '',
        postalCode: newOrg.postalCode || '',
        country: newOrg.country || '',
      },
    })
  }

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Company created successfully',
    life: 3000,
  })

  // Close the dialog
  showCreateOrganizationDialog.value = false
}

function onOrganizationUpdated(updatedOrg: Organization) {
  // Update the organization in the store
  if (!updatedOrg.id) return
  organizationStore.update(updatedOrg.id, updatedOrg)

  // Update the transaction in the store if this is the selected organization
  if (selectedOrganizationId.value === updatedOrg.id && transaction.value.id) {
    transactionStore.updateDraft({
      organizationSnapshot: {
        name: updatedOrg.name || '',
        phone: updatedOrg.phone || '',
        email: updatedOrg.email || '',
        streetAddress: updatedOrg.address || '',
        city: updatedOrg.city || '',
        state: updatedOrg.state || '',
        postalCode: updatedOrg.postalCode || '',
        country: updatedOrg.country || '',
      },
    })
  }

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Company updated successfully',
    life: 3000,
  })

  // Close the dialog
  showEditOrganizationDialog.value = false
}

async function openEditOrganizationDialog() {
  // Fetch the selected organization
  const org = await organizationStore.get(selectedOrganizationId.value)

  if (org) {
    // Create a copy of the organization for editing
    editingOrganization.value = { ...org } as any
    showEditOrganizationDialog.value = true
  } else {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Could not find company details',
      life: 3000,
    })
  }
}

async function cancel() {
  if (transactionStore.hasUnsavedChanges) {
    // Show confirmation dialog before discarding changes
    confirm.require({
      message: 'You have unsaved changes. Are you sure you want to discard them?',
      header: 'Discard Changes',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger',
      accept: async () => {
        await transactionStore.cancelEditing()
        router.push('/transaction')
      },
      reject: () => {
        // User cancelled, stay on the page
      },
    })
  } else {
    await transactionStore.cancelEditing()
    router.push('/transaction')
  }
}

function addNewItem() {
  transactionStore.initNewItem()
  editingItemIndex.value = -1
  showItemDialog.value = true
}

function editItem(index: number) {
  transactionStore.editItem(index)
  editingItemIndex.value = index
  showItemDialog.value = true
}

function removeItem(index: number) {
  transactionStore.removeItem(index)
}

function onItemSaved() {
  if (transactionModel.value) {
    transactionStore.updateDraft({
      total_amount: transactionModel.value.totalAmountNumber,
    })
  }
  // Any additional logic after saving
  showItemDialog.value = false
}

function onItemCancelled() {
  // Any additional logic after cancelling
  showItemDialog.value = false
}

function handleBulkUpdate(updates: Array<{ index: number; updates: any }>) {
  try {
    // Apply bulk updates to the transaction items
    const updatedItems = [...(transaction.value.items || [])]

    updates.forEach(({ index, updates: itemUpdates }) => {
      if (index >= 0 && index < updatedItems.length) {
        // Apply the updates to the specific item
        updatedItems[index] = {
          ...updatedItems[index],
          ...itemUpdates,
        }

        // If weight was updated, recalculate total_price
        if (itemUpdates.weight || itemUpdates.price_per_lb) {
          const item = updatedItems[index]
          const price = Number(item.price_per_lb) || 0
          const netWeight = Number(item.weight?.net) || 0
          updatedItems[index].total_price = parseFloat((price * netWeight).toFixed(2))
        }
      }
    })

    // Update the transaction in the store
    transactionStore.updateDraft({
      items: updatedItems,
    })

    // Recalculate total amount
    if (transactionModel.value) {
      transactionStore.updateDraft({
        total_amount: transactionModel.value.totalAmountNumber,
      })
    }

    toast.add({
      severity: 'success',
      summary: 'Bulk Update Complete',
      detail: `Updated ${updates.length} item(s) successfully`,
      life: 3000,
    })
  } catch (error) {
    console.error('Error handling bulk update:', error)
    toast.add({
      severity: 'error',
      summary: 'Bulk Update Error',
      detail: 'Failed to update items. Please try again.',
      life: 5000,
    })
  }
}

async function saveTransaction() {
  try {
    if (!selectedOrganizationId.value) {
      toast.add({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Please select or create a company for this transaction',
        life: 3000,
      })
      return
    }

    if (transaction.value.id) {
      await transactionStore.saveChanges()
    }

    toast.add({
      severity: 'success',
      summary: 'Transaction Saved',
      detail: 'Transaction has been successfully saved',
      life: 3000,
    })

    // Navigate to the transaction detail page
    router.push(`/transaction/${transaction.value.id}`)
  } catch (error) {
    console.error('Error saving transaction:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save transaction. Please try again.',
      life: 5000,
    })
  }
}

// Load data on component mount
onMounted(async () => {
  try {
    // Load transaction data
    await transactionStore.startEditing(transactionId.value)
    console.log('Transaction loaded for editing:', transactionStore.draft)

    if (!transactionStore.draft) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not load transaction for editing',
        life: 3000,
      })
      router.push('/transaction')
      return
    }

    // Initialize local transaction details from loaded transaction
    if (transactionStore.draft.transaction_date) {
      // Handle Firestore Timestamp or Date object
      const dateValue = transactionStore.draft.transaction_date
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
        localTransactionDate.value = (dateValue as { toDate: () => Date }).toDate()
      } else if (dateValue instanceof Date) {
        localTransactionDate.value = dateValue
      } else {
        localTransactionDate.value = new Date(dateValue as string | number)
      }
    }
    if (transactionStore.draft.notes) {
      localNotes.value = transactionStore.draft.notes
    }

    // Ensure transaction type is set to 'sale'
    if (transactionStore.draft.id) {
      transactionStore.updateDraft({ transaction_type: 'sale' })
    }

    // Fetch commodity types
    commodityTypes.value = await commodityTypeService.getActiveCommodityTypes()

    // Set local state from the loaded transaction
    if (transactionStore.draft.organizationId) {
      selectedOrganizationId.value = transactionStore.draft.organizationId

      // Fetch the organization by id
      const org = await organizationStore.get(selectedOrganizationId.value)
      if (org) {
        selectedOrganization.value = org
      }
    }
  } catch (error) {
    console.error('Error loading data:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail:
        'Failed to load necessary data: ' +
        (error instanceof Error ? error.message : String(error)),
      life: 5000,
    })
  }
})
</script>

<style scoped>
.transaction-page {
  padding: 1rem;
}
</style>
